<!--
  One episode: still, number, title, air date, and the synopsis — the things you
  actually use to decide "is this the one where…". Tapping it plays it.

  ── THE ARROW ON THE RIGHT ─────────────────────────────────────────────────────

  A phone gets a per-episode download control: a down-arrow that becomes a
  progress ring while it runs and a tick when it is here. It is a real <button>
  INSIDE what is otherwise a single <a>, which HTML does not allow — a button
  nested in an anchor is invalid and browsers do unpredictable things with the
  press. So the row is a grid whose LINK is one cell and whose button is another,
  siblings rather than parent and child; the link still covers the still and all
  the text, which is everything a thumb aiming at "play this" would hit.

  Phone only, by `shell.phone`: a desktop episode list already has the download
  action on the title page's row above it, and a fifth control per row on a
  1520px page is clutter rather than reach.
-->
<script lang="ts">
  import type { Episode } from '$types';
  import Art from './Art.svelte';
  import Icon from './Icon.svelte';
  import { airDate, clock, episodeCode, remaining } from './format';
  import { watchHref } from './nav.svelte';
  import { resumeFor } from './prefs.svelte';
  import { shell } from './shell.svelte';
  import { downloadKey, percentOf } from '$lib/offline/index-state';
  import { downloads, enqueue, entryFor, toggle as toggleDownload } from '$lib/offline/manager.svelte';

  interface Props {
    episode: Episode;
    /** Advisory runtime from the series metadata, e.g. "49 min". */
    runtimeText?: string | undefined;
    /** Highlighted because the URL points at it. */
    current?: boolean;
    /** Series name, for the download row's label. Absent hides the arrow. */
    seriesName?: string | undefined;
    /** Series poster, so a Downloads row has a picture without a lookup. */
    poster?: string | undefined;
  }

  let { episode, runtimeText, current = false, seriesName, poster }: Props = $props();

  const ref = $derived({
    kind: 'series' as const,
    imdbId: episode.seriesId,
    season: episode.season,
    episode: episode.episode,
    title: seriesName ?? episode.title
  });

  /* Touching `downloads.items` is what subscribes this row to the index: the
     manager replaces the array on every chunk, and `entryFor` alone reads
     through a function the compiler cannot see into. */
  const download = $derived.by(() => {
    void downloads.items;
    return entryFor(downloadKey(ref));
  });

  const downloadState = $derived(download?.state ?? null);
  /* The file is being rewritten to MP4 on this device so the phone can open it
     (lib/offline/remux.ts). The arrow is the same control doing the same thing;
     only its name changes, because "Stop downloading" is wrong about what the
     press would stop. */
  const converting = $derived(downloadState === 'downloading' && download?.convertedSec !== undefined);

  function askDownload(): void {
    if (download && download.state !== 'failed') {
      // A done episode has nothing left to ask for here; the Downloads screen
      // owns delete, because deleting is not something a stray thumb should do
      // from inside a list you were scrolling.
      if (download.state !== 'done') toggleDownload(download.key);
      return;
    }
    enqueue({
      ref,
      title: seriesName ?? episode.title,
      episodeLabel: `${episodeCode(episode.season, episode.episode)} · ${episode.title}`,
      ...(poster ? { poster } : {})
    });
  }

  const href = $derived(
    watchHref({
      kind: 'series',
      imdbId: episode.seriesId,
      season: episode.season,
      episode: episode.episode
    })
  );

  const progress = $derived(
    resumeFor({ kind: 'series', imdbId: episode.seriesId, season: episode.season, episode: episode.episode })
  );
  const percent = $derived(
    progress && progress.durationSec > 0 ? Math.min(100, (progress.positionSec / progress.durationSec) * 100) : 0
  );
  const aired = $derived(airDate(episode.released));
  const future = $derived(!!episode.released && new Date(episode.released).getTime() > Date.now());
</script>

<div class="row" class:current class:future>
  <a class="ep" {href} aria-current={current ? 'true' : undefined}>
    <div class="still">
      <Art
        src={episode.thumbnail}
        alt="{episode.title} still"
        label={episode.title}
        ratio="16 / 9"
        rounded={false}
      />
      <span class="play" aria-hidden="true"><Icon name="play" size={15} /></span>
      {#if percent > 0}
        <div class="bar" aria-hidden="true"><i style:width="{percent}%"></i></div>
      {/if}
    </div>

    <div class="text">
      <p class="head">
        <span class="code">{episodeCode(episode.season, episode.episode)}</span>
        <span class="name">{episode.title}</span>
      </p>
      <p class="sub">
        {#if progress && !progress.finished && percent > 0}
          <span class="resume">{remaining(progress.positionSec, progress.durationSec)}</span>
        {:else if progress?.finished}
          <span class="watched"><Icon name="check" size={12} /> Watched</span>
        {/if}
        {#if aired}<span>{future ? `Airs ${aired}` : aired}</span>{/if}
        {#if runtimeText}<span>{runtimeText}</span>{/if}
        {#if progress?.finished === false && progress.durationSec > 0}
          <span class="mono">{clock(progress.positionSec)}</span>
        {/if}
      </p>
      {#if episode.overview}
        <p class="blurb">{episode.overview}</p>
      {/if}
    </div>
  </a>

  {#if shell.phone && seriesName && !future}
    <button
      type="button"
      class="get"
      class:busy={downloadState === 'downloading' || downloadState === 'queued'}
      class:here={downloadState === 'done'}
      style="--pct: {download ? percentOf(download) : 0}"
      onclick={askDownload}
      aria-label={downloadState === 'done'
        ? `${episodeCode(episode.season, episode.episode)} is downloaded`
        : converting
          ? `Stop converting ${episodeCode(episode.season, episode.episode)}`
          : downloadState === 'downloading' || downloadState === 'queued'
            ? `Stop downloading ${episodeCode(episode.season, episode.episode)}`
            : `Download ${episodeCode(episode.season, episode.episode)}`}
    >
      <Icon
        name={downloadState === 'done' ? 'downloaded' : downloadState === 'failed' ? 'refresh' : 'download'}
        size={19}
      />
    </button>
  {/if}
</div>

<style>
  /* The link and the arrow are siblings, not nested — see the header note. The
     link takes the column the whole row used to, so the target a thumb aiming at
     "play this episode" hits is unchanged. */
  .row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    border-radius: var(--radius);
    transition: background-color 140ms var(--ease);
  }
  .row:hover {
    background: var(--surface-1);
  }
  .row.current {
    background: color-mix(in oklab, var(--focus-ring) 9%, transparent);
  }
  .row.future {
    opacity: 0.55;
  }

  .ep {
    display: grid;
    grid-template-columns: 132px 1fr;
    gap: 12px;
    padding: 10px var(--page-pad);
    border-radius: var(--radius);
    min-width: 0;
  }

  /* The per-episode download arrow. Sits in the row's right-hand column, inside
     the page pad the link already respects, and never below the 44px floor. */
  .get {
    position: relative;
    display: grid;
    place-content: center;
    width: var(--hit);
    height: var(--hit);
    flex: none;
    margin-right: calc(var(--page-pad) - 6px);
    border-radius: var(--radius-pill);
    background: none;
    color: var(--text-faint);
    touch-action: manipulation;
    transition: color var(--dur-fast) var(--ease);
  }
  .get:hover {
    color: var(--text);
  }
  .get.here {
    color: var(--good);
  }
  .get:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: -3px;
  }
  /* The same conic ring as the Downloads screen and the title page's icon row,
     so one download's progress looks the same in all three places. */
  .get.busy {
    color: var(--text);
  }
  .get.busy::before {
    content: '';
    position: absolute;
    inset: 0;
    margin: auto; /* centred; see Downloads.svelte for why */
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: conic-gradient(
      var(--accent) calc(var(--pct, 0) * 1%),
      color-mix(in oklab, var(--text-faint) 34%, transparent) 0
    );
    -webkit-mask: radial-gradient(circle, transparent 13px, #000 13.5px);
    mask: radial-gradient(circle, transparent 13px, #000 13.5px);
  }
  .get :global(svg) {
    position: relative;
  }

  .still {
    position: relative;
    border-radius: 7px;
    overflow: hidden;
    background: var(--surface-2);
  }
  .play {
    position: absolute;
    inset: 0;
    display: grid;
    place-content: center;
    color: var(--text);
    background: rgb(6 8 12 / 0.35);
    opacity: 0;
    transition: opacity 160ms var(--ease);
  }
  .row:hover .play,
  .ep:focus-visible .play {
    opacity: 1;
  }

  .bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3px;
    background: rgb(0 0 0 / 0.55);
  }
  .bar i {
    display: block;
    height: 100%;
    background: var(--accent);
  }

  .text {
    min-width: 0;
    display: grid;
    align-content: start;
    gap: 3px;
  }

  .head {
    display: flex;
    align-items: baseline;
    gap: 7px;
    min-width: 0;
  }
  .code {
    flex: none;
    color: var(--text-faint);
    font-family: var(--mono);
    font-size: 0.7rem;
    letter-spacing: 0.02em;
  }
  .name {
    font-size: 0.87rem;
    font-weight: 600;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sub {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 3px 8px;
    color: var(--text-faint);
    font-size: 0.72rem;
  }
  .sub .resume {
    color: var(--accent-hot);
    font-weight: 600;
  }
  .sub .watched {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    color: var(--good);
  }
  .sub .mono {
    font-family: var(--mono);
    font-size: 0.68rem;
  }

  .blurb {
    margin-top: 2px;
    color: var(--text-dim);
    font-size: 0.79rem;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  @media (min-width: 640px) {
    .ep {
      grid-template-columns: 190px 1fr;
      gap: 16px;
      padding-block: 12px;
    }
    .name {
      font-size: 0.92rem;
    }
    .blurb {
      -webkit-line-clamp: 3;
      line-clamp: 3;
    }
  }
</style>